import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import type { ButtonHTMLAttributes } from "react";
import { cn } from "../../lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-semibold transition-colors disabled:pointer-events-none disabled:shadow-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Brand identity: primary Blue with white, secondary Lavender with Violet Deep,
        // disabled neutral without hover.
        primary:
          "bg-brand-600 text-white shadow-sm hover:bg-brand-700 disabled:bg-zinc-200 disabled:text-zinc-500",
        secondary:
          "bg-lavender text-secondary-strong hover:bg-violet-200 disabled:bg-zinc-100 disabled:text-zinc-400",
        ghost: "text-zinc-700 hover:bg-zinc-100 hover:text-zinc-900 disabled:opacity-50",
        danger:
          "bg-red-600 text-white shadow-sm hover:bg-red-700 disabled:bg-zinc-200 disabled:text-zinc-500",
        link: "text-brand-600 underline-offset-4 hover:underline disabled:opacity-50",
      },
      size: {
        sm: "h-8 px-3",
        md: "h-9 px-4",
        lg: "h-11 px-5 text-base",
        icon: "size-9",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

export function Button({
  className,
  variant,
  size,
  asChild,
  loading,
  children,
  disabled,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled || loading}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {loading ? <Loader2 className="animate-spin" /> : null}
          {children}
        </>
      )}
    </Comp>
  );
}
